import React from 'react';
import { Accordion, Col, ProgressBar, Row } from 'react-bootstrap';
import PropTypes from 'prop-types';

import InfoTooltip from '@/components/common/InfoTooltip';

import './TractComparisonCard.css';

const currency = (value) => `$${Math.round(value).toLocaleString('en-US')}`;
const number = (value) => Math.round(value).toLocaleString('en-US');
const moeText = (moe) =>
    moe > 0 ? `± ${number(moe)}` : '±0 (controlled total)';

const TENURE_SEGMENTS = [
    { key: 'owner', label: 'Owner-occupied', variant: 'primary' },
    { key: 'renter', label: 'Renter-occupied', variant: 'secondary' },
];

const RACE_SEGMENTS = [
    { key: 'white', label: 'White, non-Hispanic', variant: 'primary' },
    { key: 'hispanic', label: 'Hispanic/Latino', variant: 'success' },
    { key: 'black', label: 'Black, non-Hispanic', variant: 'warning' },
    { key: 'asian', label: 'Asian, non-Hispanic', variant: 'info' },
    { key: 'other', label: 'Other', variant: 'secondary' },
];

const estimateShape = PropTypes.shape({
    value: PropTypes.number.isRequired,
    moe: PropTypes.number.isRequired,
    pct: PropTypes.number,
});

/**
 * Two horizontal bars (tract, county) scaled against a shared max so
 * lengths are visually comparable.
 */
function IncomeComparisonBar({ tract, county }) {
    const max = Math.max(tract.value, county.value) * 1.1;
    const rows = [
        { label: 'This tract', geo: tract, variant: 'primary' },
        { label: 'County', geo: county, variant: 'secondary' },
    ];

    return (
        <>
            {rows.map(({ label, geo, variant }) => (
                <div className="community-income-row mb-1" key={label}>
                    <div className="d-flex justify-content-between small fw-semibold mb-1">
                        <span>{label}</span>
                        <span>
                            {currency(geo.value)} {moeText(geo.moe)}
                        </span>
                    </div>
                    <ProgressBar>
                        <ProgressBar
                            now={(geo.value / max) * 100}
                            variant={variant}
                            aria-label={`${label} median household income: ${currency(geo.value)}, margin of error ${moeText(geo.moe)}`}
                        />
                    </ProgressBar>
                </div>
            ))}
        </>
    );
}

IncomeComparisonBar.propTypes = {
    tract: estimateShape.isRequired,
    county: estimateShape.isRequired,
};

/**
 * A stacked progress bar per geography (tract above county), one segment
 * per category, plus a text legend with the sourced count and margin of
 * error for every segment (bars are the visual layer; the legend numbers
 * are the authoritative sourced data).
 */
function StackedComparisonBar({ segments, tract, county }) {
    const rows = [
        { label: 'This tract', geo: tract },
        { label: 'County', geo: county },
    ];

    return (
        <>
            {rows.map(({ label, geo }) => (
                <div className="community-stacked-row mb-1" key={label}>
                    <div className="small fw-semibold mb-1">{label}</div>
                    <ProgressBar className="community-stacked-bar">
                        {segments.map((seg) => (
                            <ProgressBar
                                key={seg.key}
                                now={geo[seg.key].pct}
                                variant={seg.variant}
                                label={`${geo[seg.key].pct.toFixed(1)}%`}
                                aria-label={`${label} ${seg.label}: ${number(geo[seg.key].value)} people, margin of error ${moeText(geo[seg.key].moe)}, ${geo[seg.key].pct.toFixed(1)}%`}
                            />
                        ))}
                    </ProgressBar>
                    <ul className="community-stacked-legend list-unstyled small text-muted mb-0 d-flex flex-wrap gap-2">
                        {segments.map((seg) => (
                            <li key={seg.key}>
                                {seg.label}: {number(geo[seg.key].value)}{' '}
                                {moeText(geo[seg.key].moe)} (
                                {geo[seg.key].pct.toFixed(1)}%)
                            </li>
                        ))}
                    </ul>
                </div>
            ))}
        </>
    );
}

StackedComparisonBar.propTypes = {
    segments: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
            variant: PropTypes.string.isRequired,
        })
    ).isRequired,
    tract: PropTypes.objectOf(estimateShape).isRequired,
    county: PropTypes.objectOf(estimateShape).isRequired,
};

/**
 * TractComparisonCard
 * Compares one data center facility's census tract against its county
 * average across median household income, housing tenure, and race/
 * ethnicity, using ACS 5-year estimates with margins of error.
 *
 * Renders as an Accordion.Item, so it must be rendered inside a
 * react-bootstrap Accordion ancestor (it's not usable standalone).
 */
export default function TractComparisonCard({ facility, eventKey }) {
    const {
        name,
        address,
        county,
        tractLabel,
        medianIncome,
        tenure,
        raceEthnicity,
    } = facility;

    return (
        <Accordion.Item eventKey={eventKey} className="community-picture-card">
            <Accordion.Header as="h4">
                <span>
                    <span className="d-block fs-5 fw-semibold mb-1">
                        {name}
                    </span>
                    <span className="d-block text-muted small">
                        {address} · {county} · {tractLabel}
                    </span>
                </span>
            </Accordion.Header>
            <Accordion.Body className="py-2">
                {/* Scoped to "controlled total" only: the ± margin-of-error
                    notation is already covered by the methodology note above
                    the accordion, so repeating it here would duplicate it. */}
                <p className="small text-muted mb-2">
                    Controlled totals
                    <InfoTooltip text="A controlled total is a figure the Census Bureau fixes to a known population count, so it has no margin of error. These appear as ±0 below." />
                </p>
                <Row className="g-3 g-md-4">
                    <Col xs={12} md={4} className="community-metric">
                        <h5 className="fs-6 fw-semibold mb-1">
                            <i
                                className="bi bi-cash-coin me-2 text-primary"
                                aria-hidden="true"
                            />
                            Median Household Income
                        </h5>
                        <IncomeComparisonBar
                            tract={medianIncome.tract}
                            county={medianIncome.county}
                        />
                    </Col>

                    <Col xs={12} md={4} className="community-metric">
                        <hr className="d-md-none my-1" />
                        <h5 className="fs-6 fw-semibold mb-1">
                            <i
                                className="bi bi-house-door me-2 text-primary"
                                aria-hidden="true"
                            />
                            Housing Tenure
                        </h5>
                        <StackedComparisonBar
                            segments={TENURE_SEGMENTS}
                            tract={tenure.tract}
                            county={tenure.county}
                        />
                    </Col>

                    <Col xs={12} md={4} className="community-metric">
                        <hr className="d-md-none my-1" />
                        <h5 className="fs-6 fw-semibold mb-1">
                            <i
                                className="bi bi-people me-2 text-primary"
                                aria-hidden="true"
                            />
                            Race &amp; Ethnicity
                        </h5>
                        <StackedComparisonBar
                            segments={RACE_SEGMENTS}
                            tract={raceEthnicity.tract}
                            county={raceEthnicity.county}
                        />
                    </Col>
                </Row>
            </Accordion.Body>
        </Accordion.Item>
    );
}

TractComparisonCard.propTypes = {
    facility: PropTypes.shape({
        name: PropTypes.string.isRequired,
        address: PropTypes.string.isRequired,
        county: PropTypes.string.isRequired,
        tractLabel: PropTypes.string.isRequired,
        medianIncome: PropTypes.shape({
            tract: estimateShape.isRequired,
            county: estimateShape.isRequired,
        }).isRequired,
        tenure: PropTypes.shape({
            tract: PropTypes.objectOf(estimateShape).isRequired,
            county: PropTypes.objectOf(estimateShape).isRequired,
        }).isRequired,
        raceEthnicity: PropTypes.shape({
            tract: PropTypes.objectOf(estimateShape).isRequired,
            county: PropTypes.objectOf(estimateShape).isRequired,
        }).isRequired,
    }).isRequired,
    eventKey: PropTypes.string.isRequired,
};
